// resolveBillingError.ts maps a billing error returned by bff-web (RET-1372)
// to a localized, actionable message. It is shared by every in-app payment flow
// (subscription purchase, Lead Agent tier acceptance) so a bank decline reads the
// same actionable way everywhere — and a raw Stripe code is never shown.
//
// The `t` passed in MUST be bound to the `settings/SubscriptionManager` namespace,
// which owns the billing error copy (the legacy decline map keyed on the composite
// "{stripe_code}_{decline_code}" string, plus the generic fallbacks added for this
// fix). Callers in other namespaces obtain it via useTranslation('settings/SubscriptionManager').

import type { TFunction } from 'i18next';

// The shape bff-web forwards for a billing error. leadbilling emits the structured
// fields (RET-1372); `message`/`error_code` are the legacy fields kept for back-compat.
export type tBillingErrorPayload = {
  message?: string;
  error?: string;
  decline_code?: string;
  stripe_code?: string;
  message_key?: string;
  error_code?: string;
};

const PURCHASE_IN_PROGRESS = 'purchase_in_progress';

// RET-1905: leadbilling's error_code for a plan+frequency change it cannot apply at
// all — Stripe rejects the mixed-interval payload under classic billing mode, or the
// RET-1309 fail-fast trips on a subscription carrying surplus items. Deterministic:
// it fails identically on every retry, and no card is ever charged.
const PLAN_FREQUENCY_CHANGE_UNSUPPORTED = 'PLAN_FREQUENCY_CHANGE_UNSUPPORTED';

/**
 * Recover the Stripe error code from a composite that has no dedicated key.
 *
 * leadbilling formats every decline as "{stripe_code}_{decline_code}"
 * (PaymentDeclineClassifier::legacyMessage). The locale only carries composites
 * for the `card_declined_*` family, so a decline whose Stripe code is itself
 * specific — `incorrect_cvc` + decline_code `incorrect_cvc` gives
 * "incorrect_cvc_incorrect_cvc" — matched nothing and fell through to a generic
 * message, even though `errors.incorrect_cvc` was sitting in the locale unused.
 *
 * Walk the `_` boundaries longest-first and return the first PREFIX that has
 * copy. The prefix is the Stripe code, which is the informative half: the
 * decline_code suffix is frequently the connector's `generic_decline` filler.
 *
 * Only ever returns copy that already exists, so an unrecognized composite is
 * unaffected and still reaches the actionable bank-decline fallback below. It
 * cannot hijack the `card_declined_*` family either: bare `card_declined` is not
 * a key, so an unmapped bank decline keeps its `bank_decline_generic` copy.
 */
/**
 * Copy for a single Stripe code, or null when the locale has none.
 * Shared by the structured-field lookup and the composite prefix probe.
 */
function resolveCodeCopy(
  code: string | null | undefined,
  t: TFunction,
): string | null {
  if (!code) return null;
  const key = `errors.${code}`;
  const resolved = t(key);
  // react-i18next returns the key itself when the translation is missing.
  return resolved !== key ? resolved : null;
}

function resolveStripeCodePrefixCopy(
  composite: string,
  t: TFunction,
): string | null {
  const parts = composite.split('_');
  for (let i = parts.length - 1; i > 0; i -= 1) {
    const key = `errors.${parts.slice(0, i).join('_')}`;
    const resolved = t(key);
    // react-i18next returns the key itself when the translation is missing.
    if (resolved !== key) return resolved;
  }
  return null;
}

/**
 * Resolution order:
 *   1. in-flight duplicate (purchase_in_progress) → calming wait message
 *   2. specific localized copy keyed on the legacy composite ("card_declined_x")
 *   3. deterministic plan+frequency incompatibility → route to support, never retry
 *   4. structured decline_code / stripe_code from PaymentDeclinedException
 *   5. composite with no key but a known Stripe-code prefix → that code's copy
 *   6. recognized bank decline without specific copy → actionable generic
 *      (also the Ecuador+USD path: suggests another issuer's card, never a transfer)
 *   7. last resort → generic billing failure (never the raw code)
 */
/**
 * The composite-lookup step alone (RET-1972). The Lead Agent charge modals
 * receive the decline as a 200 `{status:'failed', message}` where `message`
 * carries the legacy composite code ("card_declined_insufficient_funds") —
 * showing it raw violates this module's doctrine. They resolve the specific
 * copy here and keep their OWN generic (`errors.charge` in their namespace)
 * as the fallback, so this returns null instead of a generic.
 *
 * `t` MUST be bound to `settings/SubscriptionManager`, same as above.
 */
export function resolveDeclineCopy(
  message: string | null | undefined,
  t: TFunction,
): string | null {
  if (!message) return null;
  const key = `errors.${message}`;
  const resolved = t(key);
  // react-i18next returns the key itself when the translation is missing.
  return resolved !== key ? resolved : null;
}

/**
 * The legacy composite code ("card_declined_insufficient_funds") this payload maps to.
 *
 * Extracted so the decline predicate and the message resolver read the SAME string;
 * the upgrade flow prefixes its code, and two copies of that strip would drift.
 */
function resolveCompositeCode(
  error: tBillingErrorPayload | undefined,
): string | undefined {
  return error?.error_code === 'UPGRADE_ERROR'
    ? error?.message?.replace(/^Payment failed: /, '')
    : error?.message;
}

/**
 * Whether this failure is the bank saying no, as opposed to a server error, a
 * duplicate in flight, or a deterministic incompatibility.
 *
 * KTD17 — this test already existed inside `resolveBillingErrorMessage`, but only to
 * pick display text. The seat flow has to ACT on it: a decline is the one failure
 * where offering to retry or switch cards makes sense, and re-deriving Stripe's error
 * shape at the call site would be a second copy of this rule that quietly diverges.
 *
 * Deliberately excludes `PLAN_FREQUENCY_CHANGE_UNSUPPORTED`, which fails identically
 * on every retry and never reaches a bank — offering "try again" there would be a lie.
 */
export function isBankDeclineError(
  error: tBillingErrorPayload | undefined,
): boolean {
  if (error?.error_code === PLAN_FREQUENCY_CHANGE_UNSUPPORTED) return false;

  const composite = resolveCompositeCode(error);

  return (
    error?.error === 'payment_declined' ||
    error?.error_code === 'UPGRADE_ERROR' ||
    Boolean(error?.decline_code) ||
    (composite?.startsWith('card_declined') ?? false)
  );
}

/**
 * Adapt a Stripe.js error (the `result.error` of `confirmCardPayment`) to the
 * payload this module resolves.
 *
 * A 3D-Secure failure never reaches leadbilling, so it arrives with `code` and
 * `decline_code` as separate fields plus an ENGLISH `message` — and that English
 * sentence was being shown verbatim in a Spanish UI. Rebuild the same
 * "{code}_{decline_code}" composite leadbilling would have produced so the 3DS
 * path resolves to exactly the same localized copy as every other decline.
 */
export function stripeJsErrorToBillingPayload(error: {
  code?: string;
  decline_code?: string;
  message?: string;
}): tBillingErrorPayload {
  const code = error.code ?? '';
  const declineCode = error.decline_code ?? '';
  const composite =
    code && declineCode ? `${code}_${declineCode}` : code || declineCode;

  return {
    error: declineCode ? 'payment_declined' : undefined,
    message: composite,
    decline_code: error.decline_code,
  };
}

export function resolveBillingErrorMessage(
  error: tBillingErrorPayload | undefined,
  t: TFunction,
): string {
  const inProgressCode = error?.error_code ?? error?.message;
  if (inProgressCode === PURCHASE_IN_PROGRESS) {
    return t('errors.purchaseInProgress');
  }

  // The upgrade flow's leadbilling response (error_code UPGRADE_ERROR) prefixes
  // the decline code as "Payment failed: <stripe_decline_code>" (RET-1475),
  // unlike the create/addon flows that send the bare code. Strip the prefix —
  // scoped to that flow — so the code matches the errors.card_declined_* keys
  // without altering the lookup key for any other caller of this shared resolver.
  const composite = resolveCompositeCode(error);
  if (composite) {
    const key = `errors.${composite}`;
    const resolved = t(key);
    // react-i18next returns the key itself when the translation is missing.
    if (resolved !== key) return resolved;
  }

  // RET-1905: a deterministic plan+frequency incompatibility is not a payment
  // problem. It must not read as a bank decline (which blames the customer's issuer
  // for a charge that never reached a bank) nor as errors.generic (which invites the
  // blind retry the backend explicitly rules out). Placed AFTER the composite lookup
  // so a real decline carrying dedicated copy still wins, and BEFORE isBankDecline
  // so this code never reaches the decline branch. The UPGRADE_ERROR limb below is
  // deliberately left intact: it is still the only net catching a genuine decline
  // whose "{code}_{decline_code}" composite has no dedicated key (e.g.
  // expired_card_generic_decline), which would otherwise fall through to generic.
  if (error?.error_code === PLAN_FREQUENCY_CHANGE_UNSUPPORTED) {
    return t('errors.plan_frequency_change_unsupported');
  }

  // Authoritative structured fields, when the decline came through
  // PaymentDeclinedException: TiersSubscriptionController returns `decline_code`
  // and `stripe_code` alongside the legacy composite. Prefer them over parsing
  // the composite — no string surgery, and they say exactly what Stripe said.
  // decline_code first (the classifier's own precedence), then stripe_code,
  // because the connector substitutes the useless `generic_decline` filler when
  // Stripe sends no decline code, and there the Stripe code is the informative
  // half (expired_card + generic_decline).
  const structuredCopy =
    resolveCodeCopy(error?.decline_code, t) ??
    resolveCodeCopy(error?.stripe_code, t);
  if (structuredCopy) return structuredCopy;

  // Fallback for the OTHER decline path, which carries no structured fields:
  // when the invoice (not the card) fails, `checkPaymentFailure` throws a bare
  // Exception whose message is only the composite. Recover the Stripe code from
  // it. Placed AFTER the exact composite lookup and the plan-frequency branch so
  // neither changes, and BEFORE isBankDecline so a decline we can actually
  // explain stops reading as the vague "contact your bank". Strictly narrowing:
  // it only ever upgrades a case that resolves to a generic message today.
  if (composite) {
    const prefixCopy = resolveStripeCodePrefixCopy(composite, t);
    if (prefixCopy) return prefixCopy;
  }

  const isBankDecline =
    error?.error === 'payment_declined' ||
    error?.error_code === 'UPGRADE_ERROR' ||
    Boolean(error?.decline_code) ||
    (composite?.startsWith('card_declined') ?? false);
  if (isBankDecline) {
    return t('errors.bank_decline_generic');
  }

  return t('errors.generic');
}
